// The recipe of a product (restaurant mode): what one unit uses of other
// products. The ingredients' stock goes out when the dish is sold.
import { formatQty } from '@ims/core'
import type { ProductRow } from '@ims/data'
import { Button, Card, Field, IconButton, useToast } from '@ims/ui'
import { Plus, Trash2 } from '@ims/ui/icons'
import { useState } from 'react'
import { Link } from 'react-router'
import { ProductPicker, QtyInput, useLookups } from '../components.tsx'
import { errorMessage, useLive, useSettings } from '../hooks.ts'
import { money } from '../lib/format.ts'
import { recipeCost, recipeOf } from '../lib/recipes.ts'
import { unitCosts } from '../lib/reports.ts'
import { deleteOp, insertOp, newProductComponent } from '../lib/rows.ts'
import { useCan, useRepo, useWorkspace } from '../workspace.tsx'
import { finePointer } from '../lib/pointer.ts'

export function RecipeCard({ product }: { product: ProductRow }) {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const toast = useToast()
  const { current } = useWorkspace()
  const canEdit = useCan('staff')
  // Anything at cost is for admins (as in Reports).
  const seesCost = useCan('admin')
  const [picked, setPicked] = useState<ProductRow | null>(null)
  const [qtyMilli, setQtyMilli] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)

  const data = useLive(
    async (r) => {
      const rows = (
        await r.list('product_components', { where: { product_id: product.id }, limit: 500 })
      ).rows
      const usedIn = (
        await r.list('product_components', { where: { component_id: product.id }, limit: 1 })
      ).total
      const ingredients = new Map<string, ProductRow>()
      for (const c of rows) {
        const p = await r.get('products', c.component_id)
        if (p) ingredients.set(p.id, p)
      }
      const basis = (await r.reports?.costBasis(null)) ?? []
      return { rows, usedIn, ingredients, costs: unitCosts([...ingredients.values()], basis) }
    },
    [product.id],
    ['product_components', 'products', 'stock_movements'],
  ).data

  if (!data) return null
  // An ingredient of another dish has no recipe of its own (one level).
  if (data.usedIn > 0 && data.rows.length === 0) return null
  const recipe = recipeOf(data.rows)
  const cost = recipeCost(recipe, data.costs)
  const unitOf = (p: ProductRow | undefined) => lookups.unit(p?.unit_id ?? null)

  const write = async (ops: Parameters<typeof repo.commit>[0]) => {
    setBusy(true)
    try {
      await repo.commit(ops)
      return true
    } catch (error) {
      toast.error(errorMessage(error))
      return false
    } finally {
      setBusy(false)
    }
  }
  const pick = async (p: ProductRow) => {
    if (p.id === product.id) return toast.error('A product is not its own ingredient.')
    if (!p.track_stock)
      return toast.error(`${p.name} is not counted. Switch "Track stock" on for it first.`)
    const own = (await repo.list('product_components', { where: { product_id: p.id }, limit: 1 }))
      .total
    if (own > 0)
      return toast.error(`${p.name} has a recipe of its own, so it cannot be an ingredient.`)
    setPicked(p)
    setQtyMilli(null)
  }
  const add = async () => {
    if (!picked || !qtyMilli || qtyMilli <= 0) return
    const ok = await write([
      insertOp(
        'product_components',
        newProductComponent(
          { tenantId: current!.tenant_id },
          { product_id: product.id, component_id: picked.id, qty_milli: qtyMilli },
        ),
      ),
    ])
    if (ok) setPicked(null)
  }

  return (
    <Card className="mt-4 p-0">
      <div className="border-b border-border px-5 py-3">
        <h2 className="font-semibold">Recipe</h2>
        <p className="text-sm text-muted">
          What one {unitOf(product)?.symbol || 'unit'} of {product.name} uses. Selling it takes
          these out of stock.
        </p>
      </div>
      {recipe.length > 0 && (
        <ul className="divide-y divide-border">
          {recipe.map((c) => {
            const p = data.ingredients.get(c.productId)
            const unitCost = data.costs.get(c.productId)
            return (
              <li key={c.productId} className="flex items-center gap-3 px-5 py-2.5 text-sm">
                <span className="min-w-0 flex-1">
                  {p ? (
                    <Link to={`/products/${p.id}`} className="font-medium hover:underline">
                      {p.name}
                    </Link>
                  ) : (
                    <span className="text-muted">Removed product</span>
                  )}
                  {p && !p.track_stock && (
                    <span className="block text-xs text-warning">
                      Not counted, so it does not go out of stock.
                    </span>
                  )}
                </span>
                <span className="tabular shrink-0">
                  {formatQty(c.qtyMilli)} {unitOf(p)?.symbol ?? ''}
                </span>
                {seesCost && (
                  <span className="tabular w-24 shrink-0 text-right text-muted">
                    {unitCost === undefined
                      ? 'no cost'
                      : money(Math.round((unitCost * c.qtyMilli) / 1000), settings)}
                  </span>
                )}
                {canEdit && (
                  <IconButton
                    label={`Remove ${p?.name ?? 'ingredient'} from the recipe`}
                    disabled={busy}
                    onClick={() =>
                      void write(
                        data.rows
                          .filter((r) => r.component_id === c.productId)
                          .map((r) => deleteOp('product_components', r.id)),
                      )
                    }
                  >
                    <Trash2 className="size-4" />
                  </IconButton>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {seesCost && cost && (
        <p className="border-t border-border px-5 py-3 text-sm">
          <span className="font-semibold">Food cost {money(cost.paisa, settings)}</span>
          <span className="text-muted">
            {' '}
            of {money(product.sell_price_paisa, settings)}
            {product.sell_price_paisa > 0 &&
              ` (${((cost.paisa / product.sell_price_paisa) * 100).toFixed(0)}%)`}
            {!cost.complete && '. Some ingredients have no cost yet.'}
          </span>
        </p>
      )}
      {canEdit && (
        <div className="border-t border-border p-4">
          {picked ? (
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault()
                void add()
              }}
            >
              <Field
                label={`${picked.name} per ${unitOf(product)?.symbol || 'unit'}`}
                hint={
                  unitOf(picked) ? `In ${unitOf(picked)!.symbol}: 150 g is 0.15 kg.` : undefined
                }
                className="w-56"
              >
                {(id) => (
                  <QtyInput
                    id={id}
                    autoFocus={finePointer}
                    milli={qtyMilli}
                    allowsDecimal
                    onValue={setQtyMilli}
                    placeholder="e.g. 0.15"
                  />
                )}
              </Field>
              <Button
                type="submit"
                icon={<Plus className="size-4" />}
                loading={busy}
                disabled={!qtyMilli || qtyMilli <= 0}
              >
                Add ingredient
              </Button>
              <Button type="button" variant="ghost" onClick={() => setPicked(null)}>
                Cancel
              </Button>
            </form>
          ) : (
            <ProductPicker
              placeholder="Add an ingredient: search by name or code…"
              addNew="ingredient"
              onPick={(p) => void pick(p)}
            />
          )}
        </div>
      )}
    </Card>
  )
}
